import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDateTime } from '@/lib/utils/format';
import type { RecentSignal, RecentSignalKind, RecentSignalSeverity } from '@/lib/api/engagements';

/**
 * Critical signals subsection for the engagement overview (T-0097).
 *
 * Prototype intent (`screens-engagement.js → window.Screens.overview` —
 * the "Critical signals · this week" card):
 *   - left-rule posture-critical card
 *   - rows of [ID chip] [signal text] in critical-tint
 *
 * T-0119 (PR #145) added a `recent_signals` array to the engagement
 * detail DTO — a composite of posture / override / proposal / RFI /
 * deviation / score signals tagged with severity. Each entry carries
 * a one-line summary, an occurred_at timestamp, and an optional
 * source_ref for deep linking.
 */
export interface CriticalSignalsProps {
  signals: RecentSignal[];
  /** Cap on rendered rows (default 5). */
  limit?: number;
}

const SEVERITY_TONE: Record<RecentSignalSeverity, string> = {
  critical: 'bg-posture-critical-tint text-posture-critical',
  high: 'bg-posture-at-risk-tint text-posture-at-risk',
  medium: 'bg-paper-100 text-ink-700',
  low: 'bg-paper-100 text-ink-500',
  info: 'bg-paper-100 text-ink-500',
};

/** The chip on each row says what kind of change it is, in words, not the API's kind key (C2-29). */
const KIND_LABEL: Record<RecentSignalKind, string> = {
  posture_change: 'Posture',
  override: 'Override',
  proposal: 'AI proposal',
  rfi_transition: 'RFI',
  deviation: 'Deviation',
  score_change: 'Score',
  document: 'Document',
  deliverable: 'Deliverable',
  contract: 'Contract',
};

export function CriticalSignals({ signals, limit = 5 }: CriticalSignalsProps) {
  const recent = signals.slice(0, limit);
  // The red rule is reserved for when something shown is actually urgent.
  const hasUrgent = recent.some((s) => s.severity === 'critical' || s.severity === 'high');
  return (
    <section
      className={`rounded-card border border-ink-100 border-l-[3px] bg-paper-0 p-card-pad ${
        hasUrgent ? 'border-l-posture-critical' : 'border-l-ink-200'
      }`}
      aria-labelledby="critical-signals-title"
    >
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle
          className={`size-4 ${hasUrgent ? 'text-posture-critical' : 'text-ink-500'}`}
          aria-hidden="true"
        />
        <h2 id="critical-signals-title" className="t-h3">
          What changed recently
        </h2>
      </div>

      {recent.length === 0 ? (
        <p className="text-body-sm text-ink-500">
          Nothing yet. Uploads, what the AI read from them, decisions and status changes appear here
          as they happen.
        </p>
      ) : (
        <ul className="flex flex-col gap-3" role="list">
          {recent.map((s, i) => (
            <li key={`${s.kind}-${s.occurred_at}-${i}`} className="flex items-start gap-2.5">
              <Badge
                variant="secondary"
                className={`h-5 shrink-0 rounded-chip px-1.5 text-[11px] ${SEVERITY_TONE[s.severity]}`}
              >
                {KIND_LABEL[s.kind] ?? s.kind.replace(/_/g, ' ')}
              </Badge>
              <div className="min-w-0 flex-1">
                <div className="text-body-sm leading-[18px] text-ink-700">{s.summary}</div>
                <div className="t-mono-sm">{formatDateTime(s.occurred_at)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
